import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IQuestion } from "../interfaces/Question";
import apiClient from "../api/apiClient";

interface IFetchQuestion {
    programId: string;
}

interface IStoreQuestions {
    programId: string;
    questions: IQuestion[];
}

interface IDeleteQuestion {
    questionId: string;
}

interface ProgramQuestionsState {
    loading: boolean;
    error: string | null;
    programQuestions: IQuestion[] | [];
}

const initialState: ProgramQuestionsState = {
    loading: false,
    error: null,
    programQuestions: [],
};

/**
 * Axios request to fetch questions for a given programId
 */
export const fetchQuestions = createAsyncThunk('questions/fetchQuestions',
    async ({ programId }: IFetchQuestion, thunkAPI) => {
        try {
            const response = await apiClient.get(`/api/v1/programs/${programId}/questions`);
            return response.data?.data?.questions || [];
        } catch (error: any) {
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * AXIOS request for store questions
 */
export const storeQuestions = createAsyncThunk('questions/storeQuestions',
    async ({ programId, questions }: IStoreQuestions, thunkAPI) => {
        try {
            const response = await apiClient.post('/api/v1/questions', {
                programId,
                questions
            });
            thunkAPI.dispatch(fetchQuestions({ programId: programId as string }));
            return response.data?.data?.questions || [];
        } catch (error: any) {
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * Axios API request to delete a question
 */
export const deleteQuestion = createAsyncThunk('questions/deleteQuestion',
    async ({ questionId }: IDeleteQuestion, thunkAPI) => {
        try {
            const response = await apiClient.delete(`/api/v1/questions/${questionId}`);
            return response.data?.data?.questions || [];
        } catch (error: any) {
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

const programQuestionsSlice = createSlice({
    name: "programQuestions",
    initialState,
    reducers: {
        resetState: (state) => initialState
    },
    extraReducers: (builder) => {
        builder.addCase(fetchQuestions.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchQuestions.fulfilled, (state, action) => {
                state.loading = false;
                state.programQuestions = action.payload;
                state.error = null;
            })
            .addCase(fetchQuestions.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as string) || action.error.message || 'An error occurred';
            });
    },
});

export const { resetState } = programQuestionsSlice.actions;
export default programQuestionsSlice.reducer;